import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import blogService from '../../services/blogService';
import { useAnalytics, useEngagementTracking } from '../../hooks/useAnalytics';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const { trackBlogView, trackSocialShare } = useAnalytics();

  // Track engagement time for this blog post
  useEngagementTracking(`blog-${slug}`);

  useEffect(() => {
    if (slug) {
      loadBlog();
    }
  }, [slug]);

  const loadBlog = async () => {
    try {
      setLoading(true);
      const result = await blogService.getBlogBySlug(slug);

      if (result.success) {
        setBlog(result.data);
        // Track blog view
        trackBlogView(result.data.id, result.data.title, result.data.tags?.[0] || '');
        // Load related blogs
        loadRelatedBlogs(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load blog post');
      console.error('Error loading blog:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedBlogs = async (currentBlog) => {
    try {
      const result = await blogService.getPublishedBlogs();
      if (result.success) {
        // Filter out current blog and get up to 3 related blogs
        const related = result.data
          .filter(b => b.id !== currentBlog.id)
          .slice(0, 3);
        setRelatedBlogs(related);
      }
    } catch (err) {
      console.error('Error loading related blogs:', err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareOnSocial = (platform) => {
    const url = window.location.href;
    const title = blog.title;

    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`;
        break;
      default:
        return;
    }

    // Track social sharing
    trackSocialShare(platform, 'blog_post', blog.id);
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      // You could add a toast notification here
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading blog post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="py-5">
              <i className="bi bi-exclamation-triangle display-1 text-muted mb-3"></i>
              <h3 className="mb-3">Blog Post Not Found</h3>
              <p className="text-muted mb-4">
                {error || "The blog post you're looking for doesn't exist or has been removed."}
              </p>
              <div className="d-flex gap-2 justify-content-center">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => navigate(-1)}
                >
                  <i className="bi bi-arrow-left me-1"></i>
                  Go Back
                </button>
                <Link to="/blog" className="btn btn-primary">
                  View All Blogs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5" style={{ paddingTop: '120px' }}>
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4" style={{ paddingTop: '61px' }}>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/" className="text-decoration-none">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/blog" className="text-decoration-none">Blog</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {blog.title}
          </li>
        </ol>
      </nav>

      <div className="row">
        {/* Main Content */}
        <div className="col-lg-8">
          <article className="blog-post">
            {/* Featured Image */}
            {blog.featuredImage && (
              <div className="mb-4">
                <img
                  src={blog.featuredImage}
                  alt={blog.title}
                  className="img-fluid rounded shadow-sm"
                  style={{ width: '100%', height: '400px', objectFit: 'cover' }}
                  onError={(e) => {
                    console.warn('Failed to load featured image:', blog.featuredImage);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => {
                    console.log('Featured image loaded successfully');
                  }}
                />
              </div>
            )}

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="mb-3">
                {blog.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="badge bg-primary me-2 mb-1"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="display-5 fw-bold mb-3" style={{ paddingTop: '115px' }}>{blog.title}</h1>

            {/* Meta Information */}
            <div className="d-flex flex-wrap align-items-center text-muted mb-4 pb-3 border-bottom">
              <div className="me-4 mb-2">
                <i className="bi bi-person me-1"></i>
                <span>By {blog.author || 'Admin'}</span>
              </div>
              <div className="me-4 mb-2">
                <i className="bi bi-calendar3 me-1"></i>
                <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
              </div>
              <div className="me-4 mb-2">
                <i className="bi bi-clock me-1"></i>
                <span>{blog.readTime || 5} min read</span>
              </div>
            </div>

            {/* Content */}
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: blog.content }}
              style={{
                lineHeight: '1.8',
                fontSize: '1.1rem'
              }}
            />

            {/* Social Sharing */}
            <div className="mt-5 pt-4 border-top">
              <h5 className="mb-3">Share this post</h5>
              <div className="d-flex flex-wrap gap-2">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => shareOnSocial('facebook')}
                >
                  <i className="bi bi-facebook me-1"></i>
                  Facebook
                </button>
                <button
                  className="btn btn-outline-info btn-sm"
                  onClick={() => shareOnSocial('twitter')}
                >
                  <i className="bi bi-twitter me-1"></i>
                  Twitter
                </button>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => shareOnSocial('linkedin')}
                >
                  <i className="bi bi-linkedin me-1"></i>
                  LinkedIn
                </button>
                <button
                  className="btn btn-outline-success btn-sm"
                  onClick={() => shareOnSocial('whatsapp')}
                >
                  <i className="bi bi-whatsapp me-1"></i>
                  WhatsApp
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={copyToClipboard}
                >
                  <i className="bi bi-link-45deg me-1"></i>
                  Copy Link
                </button>
              </div>
            </div>
          </article>
        </div>

        {/* Sidebar */}
        <div className="col-lg-4">
          <div className="sticky-top" style={{ top: '120px' }}>
            {/* Related Posts */}
            {relatedBlogs.length > 0 && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0">
                  <h5 className="mb-0">Related Posts</h5>
                </div>
                <div className="card-body">
                  {relatedBlogs.map((relatedBlog) => (
                    <div key={relatedBlog.id} className="mb-3 pb-3 border-bottom">
                      <h6 className="mb-1">
                        <Link
                          to={`/blog/${relatedBlog.slug}`}
                          className="text-decoration-none text-dark"
                        >
                          {relatedBlog.title}
                        </Link>
                      </h6>
                      <small className="text-muted">
                        {formatDate(relatedBlog.publishedAt || relatedBlog.createdAt)}
                      </small>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Back to Blog List */}
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center">
                <Link to="/blog" className="btn btn-primary">
                  <i className="bi bi-arrow-left me-1"></i>
                  Back to All Blogs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .blog-content h1,
        .blog-content h2,
        .blog-content h3,
        .blog-content h4,
        .blog-content h5,
        .blog-content h6 {
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        
        .blog-content p {
          margin-bottom: 1.5rem;
        }
        
        .blog-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1rem 0;
        }
        
        .blog-content blockquote {
          border-left: 4px solid #7b00ff;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          background-color: #f8f9fa;
          padding: 1rem;
          border-radius: 4px;
        }
        
        .blog-content code {
          background-color: #f8f9fa;
          padding: 0.2rem 0.4rem;
          border-radius: 4px;
          font-size: 0.9em;
        }
        
        .blog-content pre {
          background-color: #f8f9fa;
          padding: 1rem;
          border-radius: 8px;
          overflow-x: auto;
          margin: 1rem 0;
        }
        
        .blog-content ul,
        .blog-content ol {
          margin-bottom: 1.5rem;
          padding-left: 2rem;
        }
        
        .blog-content li {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default BlogPost;